import React, { useRef, useState } from 'react';
import { Send, Smile, Image as ImageIcon, Loader2, Gift as GiftIcon, Star, CalendarHeart, Sparkles, AtSign } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';

const QUICK_EMOJIS = [
  '😀','😂','🥰','😍','😎','🤩','🥳','😭',
  '😅','😉','😘','🤗','🤔','😏','😴','🙄',
  '😱','🥺','😡','😤','🤯','🥶','🤤','🤠',
  '👍','👎','👏','🙌','🙏','💪','👋','🤝',
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍',
  '🔥','✨','⭐','🌟','💫','💥','💯','🎉',
  '🌹','🌸','🌻','🌈','🎁','🎂','🍕','☕',
  '🚀','🌍','🌌','🪐','👽','🌙','☀️','⚡',
];

async function compressImageToBlob(file, maxDim = 1080, quality = 0.85) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i); i.onerror = () => rej(new Error('Could not read image'));
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').drawImage(img, 0, 0, w, h);
  return await new Promise((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error('Compression failed')), 'image/jpeg', quality));
}

// Format a Date object into the value expected by <input type="datetime-local">
const fmtDTLocal = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Build smart suggested-time chips: "Tonight 7 PM", "Tomorrow 6 PM", "This Saturday 11 AM"
const buildSuggestedTimes = () => {
  const now = new Date();
  // Tonight 7 PM: today at 19:00 — if already past 7 PM, roll forward to tomorrow same slot
  const tonight = new Date(now);
  tonight.setHours(19, 0, 0, 0);
  if (tonight.getTime() <= now.getTime() + 30 * 60 * 1000) {
    tonight.setDate(tonight.getDate() + 1);
  }
  // Tomorrow 6 PM
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(18, 0, 0, 0);
  // This Saturday 11 AM (next upcoming Saturday; if today is Sat and before 11 AM use today, else next Sat)
  const saturday = new Date(now);
  const dow = saturday.getDay(); // 0 Sun … 6 Sat
  let daysAhead = (6 - dow + 7) % 7;
  saturday.setHours(11, 0, 0, 0);
  if (dow === 6 && saturday.getTime() <= now.getTime()) daysAhead = 7;
  saturday.setDate(saturday.getDate() + daysAhead);
  return [
    { label: 'Tonight 7 PM',     value: fmtDTLocal(tonight) },
    { label: 'Tomorrow 6 PM',    value: fmtDTLocal(tomorrow) },
    { label: 'This Saturday 11 AM', value: fmtDTLocal(saturday) },
  ];
};

/**
 * Shared chat composer with text + emoji + image upload, plus an OPTIONAL gift picker.
 *
 * Props:
 *   onSend(text)        async function called with the typed body OR with an image data URL
 *   placeholder         input placeholder (default: "Type a message…")
 *   testIdPrefix        prefix for data-testids (e.g. "mail" / "dating-dm")
 *   showImage           render the image-upload button (default: true)
 *   showEmoji           render the emoji-picker button (default: true)
 *   gifts               array of gifts (objects with id, name, emoji, price, rarity). Renders a gift button when present.
 *   onSendGift(gift)    async function called when a gift is picked
 *   trailing            extra buttons rendered AFTER the send button (e.g. wave)
 */
export default function ChatComposer({
  onSend,
  onTyping = null,
  placeholder = 'Type a message…',
  testIdPrefix = 'composer',
  showImage = true,
  showEmoji = true,
  gifts = null,
  onSendGift = null,
  dateInvites = null,
  onSendDateInvite = null,
  trailing = null,
  disabled = false,
}) {
  const [text, setText] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePick, setInvitePick] = useState(null);  // selected kind preset
  const [inviteWhen, setInviteWhen] = useState('');
  const [invitePlace, setInvitePlace] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleSend = async (override) => {
    const body = (override ?? text).trim();
    if (!body || busy || disabled) return;
    setBusy(true);
    try {
      await onSend(body);
      if (override === undefined) setText('');
    } finally {
      setBusy(false);
    }
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    setBusy(true);
    try {
      // Compress client-side (max 1080px @ q0.85) to keep upload cost sane,
      // then POST to /api/uploads/chat. Server returns a stable URL that we
      // ship to the message endpoint via `image_url`. This is the fix that
      // keeps new chat threads fast forever — the base64 payload never
      // touches the message document.
      const blob = await compressImageToBlob(file);
      const fd = new FormData();
      fd.append('file', blob, 'chat.jpg');
      const { data } = await api.post('/uploads/chat', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await onSend('📷 image', { image_url: data.url });
      toast.success('Image sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Could not send image');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleGiftPick = async (g) => {
    if (!onSendGift || busy) return;
    setBusy(true);
    try {
      await onSendGift(g);
      setGiftOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const resetInvite = () => {
    setInvitePick(null);
    setInviteWhen('');
    setInvitePlace('');
    setInviteNote('');
  };

  const handleSendInvite = async () => {
    if (!onSendDateInvite || !invitePick || busy) return;
    setBusy(true);
    try {
      await onSendDateInvite({
        kind: invitePick.kind,
        emoji: invitePick.emoji,
        when: inviteWhen || null,
        place: invitePlace.trim() || null,
        note: inviteNote.trim() || null,
      });
      setInviteOpen(false);
      resetInvite();
    } finally {
      setBusy(false);
    }
  };

  const showGifts = Array.isArray(gifts) && gifts.length > 0 && typeof onSendGift === 'function';
  const showInvites = Array.isArray(dateInvites) && dateInvites.length > 0 && typeof onSendDateInvite === 'function';

  return (
    <div
      className="border-t border-white/5 bg-[#10101a] px-4 py-3 flex items-center gap-2 relative shrink-0"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      {showImage && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImagePick}
            data-testid={`${testIdPrefix}-image-input`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            data-testid={`${testIdPrefix}-image-btn`}
            className="p-2 text-white/60 hover:text-cyan-300"
            aria-label="Attach image"
            disabled={busy}
          >
            <ImageIcon size={18} />
          </button>
        </>
      )}
      {showEmoji && (
        <div className="relative">
          <button
            onClick={() => { setEmojiOpen(o => !o); setGiftOpen(false); setInviteOpen(false); }}
            data-testid={`${testIdPrefix}-emoji-btn`}
            className={`p-2 transition ${emojiOpen ? 'text-amber-300' : 'text-white/60 hover:text-amber-300'}`}
            aria-label="Insert emoji"
            disabled={busy}
          >
            <Smile size={18} />
          </button>
          {emojiOpen && (
            <>
              <div className="fixed inset-0 z-[40]" onClick={() => setEmojiOpen(false)} />
              <div
                onClick={(e) => e.stopPropagation()}
                data-testid={`${testIdPrefix}-emoji-panel`}
                className="absolute bottom-12 left-0 z-[50] w-72 rounded-2xl bg-[#161622] border border-white/10 shadow-2xl p-3"
              >
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Quick emojis</div>
                <div className="grid grid-cols-8 gap-1 max-h-56 overflow-y-auto scroll-hide">
                  {QUICK_EMOJIS.map((e, i) => (
                    <button
                      key={i}
                      onClick={() => { setText(t => t + e); setEmojiOpen(false); }}
                      className="text-2xl p-1.5 rounded-lg hover:bg-white/10 transition"
                    >{e}</button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {showGifts && (
        <div className="relative">
          <button
            onClick={() => { setGiftOpen(o => !o); setEmojiOpen(false); setInviteOpen(false); }}
            data-testid={`${testIdPrefix}-gift-btn`}
            className={`p-2 transition ${giftOpen ? 'text-amber-300' : 'text-white/60 hover:text-amber-300'}`}
            aria-label="Send a gift"
            disabled={busy}
          >
            <GiftIcon size={18} />
          </button>
          {giftOpen && (
            <>
              <div className="fixed inset-0 z-[40]" onClick={() => setGiftOpen(false)} />
              <div
                onClick={(e) => e.stopPropagation()}
                data-testid={`${testIdPrefix}-gift-panel`}
                className="absolute bottom-12 left-0 z-[50] w-80 rounded-2xl bg-[#161622] border border-amber-400/20 shadow-2xl p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Send a gift</div>
                  <div className="text-[10px] text-amber-300 flex items-center gap-1"><Star size={9} fill="currentColor" />Cost = stars</div>
                </div>
                <div className="grid grid-cols-4 gap-1 max-h-64 overflow-y-auto scroll-hide">
                  {gifts.map(g => (
                    <button
                      key={g.id}
                      data-testid={`${testIdPrefix}-gift-${g.id}`}
                      onClick={() => handleGiftPick(g)}
                      className="rounded-xl p-2 bg-white/5 hover:bg-amber-400/10 border border-white/5 hover:border-amber-400/40 text-center transition"
                    >
                      <div className="text-2xl mb-0.5">{g.emoji}</div>
                      <div className="text-[10px] font-semibold truncate">{g.name}</div>
                      <div className="text-[9px] text-amber-300 flex items-center justify-center gap-0.5">
                        <Star size={7} fill="currentColor" />{g.price}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {showInvites && (
        <div className="relative">
          <button
            onClick={() => { setInviteOpen(o => !o); setEmojiOpen(false); setGiftOpen(false); }}
            data-testid={`${testIdPrefix}-invite-btn`}
            className={`p-2 transition ${inviteOpen ? 'text-rose-300' : 'text-white/60 hover:text-rose-300'}`}
            aria-label="Send a date invite"
            disabled={busy}
          >
            <CalendarHeart size={18} />
          </button>
          {inviteOpen && (
            <>
              <div className="fixed inset-0 z-[40]" onClick={() => { setInviteOpen(false); resetInvite(); }} />
              <div
                onClick={(e) => e.stopPropagation()}
                data-testid={`${testIdPrefix}-invite-panel`}
                className="absolute bottom-12 left-0 z-[50] w-80 rounded-2xl bg-[#161622] border border-rose-400/30 shadow-2xl p-3"
              >
                <div className="text-[10px] uppercase tracking-wider text-rose-200/80 mb-2 flex items-center gap-1">
                  <CalendarHeart size={11} className="text-rose-300" /> Send a date invite
                </div>
                {!invitePick ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {dateInvites.map(p => (
                        <button
                          key={p.kind}
                          data-testid={`${testIdPrefix}-invite-${p.kind.toLowerCase()}`}
                          onClick={() => setInvitePick(p)}
                          className="rounded-xl p-3 bg-white/5 hover:bg-rose-400/10 border border-white/5 hover:border-rose-400/40 text-center transition"
                        >
                          <div className="text-2xl">{p.emoji}</div>
                          <div className="text-xs font-semibold mt-1">{p.kind}</div>
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] text-white/40 mt-2 text-center">Pick a vibe — you can add when & where on the next step.</div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="text-3xl">{invitePick.emoji}</div>
                      <div className="flex-1">
                        <div className="font-display font-semibold">{invitePick.kind} date</div>
                        <button onClick={resetInvite} className="text-[10px] text-rose-300 hover:text-rose-200 underline">change</button>
                      </div>
                    </div>
                    <label className="text-[10px] uppercase tracking-wider text-white/40">When (optional)</label>
                    <div className="mt-1 mb-2 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-invite-suggested`}>
                      {buildSuggestedTimes().map(s => {
                        const active = inviteWhen === s.value;
                        return (
                          <button
                            key={s.label}
                            type="button"
                            onClick={() => setInviteWhen(s.value)}
                            data-testid={`${testIdPrefix}-invite-when-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`text-[10px] px-2.5 py-1 rounded-full border transition ${active
                              ? 'bg-rose-500/30 border-rose-400 text-white'
                              : 'bg-white/5 border-white/10 text-white/75 hover:bg-rose-400/15 hover:border-rose-400/40 hover:text-white'}`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      type="datetime-local"
                      value={inviteWhen}
                      onChange={(e) => setInviteWhen(e.target.value)}
                      data-testid={`${testIdPrefix}-invite-when`}
                      className="mb-2 bg-white/5 border-white/10 text-white text-sm h-9"
                    />
                    <label className="text-[10px] uppercase tracking-wider text-white/40">Where (optional)</label>
                    <Input
                      value={invitePlace}
                      onChange={(e) => setInvitePlace(e.target.value)}
                      placeholder="e.g. Blue Bottle, IMAX Mumbai…"
                      maxLength={120}
                      data-testid={`${testIdPrefix}-invite-place`}
                      className="mt-1 mb-2 bg-white/5 border-white/10 text-white text-sm h-9"
                    />
                    <label className="text-[10px] uppercase tracking-wider text-white/40">Note (optional)</label>
                    <Textarea
                      value={inviteNote}
                      onChange={(e) => setInviteNote(e.target.value)}
                      placeholder="Add a sweet line…"
                      maxLength={240}
                      rows={2}
                      data-testid={`${testIdPrefix}-invite-note`}
                      className="mt-1 mb-3 bg-white/5 border-white/10 text-white text-xs min-h-[52px]"
                    />
                    <button
                      onClick={handleSendInvite}
                      data-testid={`${testIdPrefix}-invite-send`}
                      disabled={busy}
                      className="w-full rounded-full py-2 text-sm font-semibold bg-gradient-to-r from-rose-500 to-pink-600 text-white disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      <Sparkles size={13} /> Send invite
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
      <Input
        value={text}
        onChange={e => {
          setText(e.target.value);
          // Fire a typing ping (throttled by the parent) whenever the composer
          // has non-empty content — never on backspace-to-empty.
          if (e.target.value.trim()) onTyping?.();
        }}
        onKeyDown={e => e.key === 'Enter' && handleSend()}
        placeholder={placeholder}
        className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40"
        data-testid={`${testIdPrefix}-text-input`}
        disabled={busy || disabled}
      />
      <button
        onClick={() => handleSend()}
        disabled={!text.trim() || busy || disabled}
        className="p-2 text-cyan-400 disabled:text-white/20"
        data-testid={`${testIdPrefix}-send-btn`}
        aria-label="Send"
      >
        {busy ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
      </button>
      {trailing}
    </div>
  );
}
